/*
 * <authenticity-verifier> — client-side check of an NPRA NOT number or batch code.
 * Data sources (no network call):
 *   1. [data-npra-row] elements anywhere on the page (the NPRA table section): data-not, data-product, data-size
 *   2. data-batches attribute on the element: lines "CODE | Product | NOT number | Expiry"
 * Shows [data-verifier-ok] or [data-verifier-bad] and fills [data-field="…"] nodes.
 */
class AuthenticityVerifier extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-verifier-input]');
    this.errorEl = this.querySelector('[data-verifier-error]');
    this.okEl = this.querySelector('[data-verifier-ok]');
    this.badEl = this.querySelector('[data-verifier-bad]');
    if (!this.form || !this.input) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.check(this.input.value);
    });
    const preset = new URLSearchParams(window.location.search).get('code');
    if (preset) {
      this.input.value = preset;
      this.check(preset);
    }
    this.querySelectorAll('[data-verifier-sample]').forEach((button) =>
      button.addEventListener('click', () => {
        this.input.value = button.dataset.verifierSample;
        this.check(this.input.value);
      })
    );
    this.querySelectorAll('[data-verifier-reset]').forEach((button) =>
      button.addEventListener('click', () => {
        this.reset();
        this.input.focus();
      })
    );
  }

  normalise(value) {
    return String(value == null ? '' : value).replace(/\s+/g, '').toUpperCase();
  }

  batches() {
    if (this._batches) return this._batches;
    const raw = this.dataset.batches || '';
    this._batches = raw
      .split(/\r?\n/)
      .map((line) => line.split('|').map((part) => part.trim()))
      .filter((parts) => parts[0])
      .map(([code, product, notNumber, expiry]) => ({
        code: this.normalise(code),
        product: product || '',
        not: notNumber ? this.normalise(notNumber) : '',
        expiry: expiry || '',
      }));
    return this._batches;
  }

  notRows() {
    return [...document.querySelectorAll('[data-npra-row]')].map((row) => ({
      not: this.normalise(row.dataset.not),
      product: row.dataset.product || '',
      size: row.dataset.size || '',
    }));
  }

  lookup(code) {
    const batch = this.batches().find((item) => item.code === code);
    if (batch) {
      const row = this.notRows().find((item) => item.not && item.not === batch.not);
      return {
        product: batch.product || (row && row.product) || '',
        not: batch.not || (row && row.not) || '',
        batch: batch.code,
        expiry: batch.expiry,
      };
    }
    const row = this.notRows().find((item) => item.not === code);
    if (row) return { product: row.product, not: row.not, batch: '', expiry: '' };
    return null;
  }

  check(raw) {
    const code = this.normalise(raw);
    this.hideResults();
    if (!code) {
      this.errorEl.textContent = this.dataset.msgEmpty || '';
      this.errorEl.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      return;
    }
    this.errorEl.hidden = true;
    this.input.removeAttribute('aria-invalid');
    const found = this.lookup(code);
    if (found) {
      this.fill(this.okEl, { product: found.product, not: found.not || '-', batch: found.batch || '-', expiry: found.expiry || '-' });
      this.okEl.hidden = false;
    } else {
      this.fill(this.badEl, { shown: String(raw).trim() });
      this.badEl.hidden = false;
    }
  }

  fill(container, values) {
    Object.entries(values).forEach(([key, value]) => {
      const el = container.querySelector(`[data-field="${key}"]`);
      if (el) el.textContent = value;
    });
  }

  hideResults() {
    this.okEl.hidden = true;
    this.badEl.hidden = true;
  }

  reset() {
    this.input.value = '';
    this.errorEl.hidden = true;
    this.input.removeAttribute('aria-invalid');
    this.hideResults();
  }
}

if (!customElements.get('authenticity-verifier')) customElements.define('authenticity-verifier', AuthenticityVerifier);
